import { createSlice } from "@reduxjs/toolkit";
import {
  doAddPrediction,
  doClearAll,
  doDelPrediction,
  doFetchPredictionList,
  doUpdatePrediction,
} from "./prediction.action";
import { Prediction } from "../config";

interface Meta {
  totalItems: number;
  itemsPerPage: number;
  totalPages: number;
  currentPage: number;
  lastPredictionUpdatedAt: number;
}

interface PredictionState {
  predictions: Prediction[];
  searchQuery: string;
  meta: Meta | null;
  loading: boolean;
  error: string | null;
}

const initialState: PredictionState = {
  predictions: [] as Prediction[],
  meta: null,
  searchQuery: "",
  loading: false,
  error: null,
};

const predictionSlice = createSlice({
  name: "prediction",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch list
    builder
      .addCase(doFetchPredictionList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doFetchPredictionList.fulfilled, (state, action) => {
        state.loading = false;
        state.predictions = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(doFetchPredictionList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // add prediction
    builder
      .addCase(doAddPrediction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doAddPrediction.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(doAddPrediction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // update prediction
    builder
      .addCase(doUpdatePrediction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doUpdatePrediction.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        const updatedPredictionIndex = state.predictions.findIndex(
          (prediction) => prediction.predictionUid === action.meta.arg.id
        );

        if (updatedPredictionIndex !== -1) {
          state.predictions[updatedPredictionIndex] = {
            ...state.predictions[updatedPredictionIndex],
            ...action.meta.arg.body,
          };
        }
      })
      .addCase(doUpdatePrediction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // delete prediction
    builder
      .addCase(doDelPrediction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doDelPrediction.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        state.predictions = state.predictions.filter(
          (prediction) => prediction.predictionUid !== action.meta.arg
        );
        if (state.meta) {
          state.meta.totalItems -= 1;
        }
      })
      .addCase(doDelPrediction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(doClearAll.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doClearAll.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.predictions = [];
      })
      .addCase(doClearAll.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default predictionSlice.reducer;
